/**
 * Tenant branding (logo URL).
 *
 * Stored client-side in localStorage keyed by tenant slug so each
 * tenant's logo persists per-device without round-tripping the API.
 * A server-side `customer_groups.logo_url` column is the natural
 * long-term home, but this client-only step ships the feature today
 * without a migration; promote to backend in a follow-up.
 *
 * Helpers:
 *   - `getBrandLogoUrl(slug)` — read the saved URL, or null.
 *   - `setBrandLogoUrl(slug, url)` — write/clear (empty string clears).
 *   - `useBrandLogoUrl(slug)` — subscribes to changes so a save in
 *     /settings/branding updates the AppShell topbar instantly.
 */

import { useEffect, useState } from "react";

import { tenantContext } from "./tenant";

const _PREFIX = "solvait.brand.logoUrl::";

function _keyFor(slug: string | null): string {
  return `${_PREFIX}${slug ?? "_default_"}`;
}

export function getBrandLogoUrl(slug: string | null): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(_keyFor(slug));
    return v && v.trim() !== "" ? v : null;
  } catch {
    return null;
  }
}

export function setBrandLogoUrl(slug: string | null, url: string): void {
  if (typeof window === "undefined") return;
  const trimmed = url.trim();
  const key = _keyFor(slug);
  try {
    if (trimmed === "") {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, trimmed);
    }
    // Notify same-tab listeners — `storage` event only fires on other
    // tabs, so we dispatch a CustomEvent for the topbar in this tab.
    window.dispatchEvent(
      new CustomEvent("solvait:branding-changed", { detail: { slug, url: trimmed } }),
    );
  } catch {
    // Storage may be unavailable (Safari private mode).
  }
}

/**
 * React hook — returns the current logo URL for a given tenant slug,
 * updating when the saved value changes (in this tab or via another
 * tab's `storage` event). Defaults to the tenant from context.
 */
export function useBrandLogoUrl(slugOverride?: string | null): string | null {
  const tenant = tenantContext();
  const slug = slugOverride ?? tenant.slug;
  const [value, setValue] = useState<string | null>(() => getBrandLogoUrl(slug));

  useEffect(() => {
    setValue(getBrandLogoUrl(slug));

    const onStorage = (e: StorageEvent) => {
      if (e.key === _keyFor(slug)) setValue(getBrandLogoUrl(slug));
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail as { slug?: string | null } | undefined;
      if (detail?.slug === slug || detail?.slug === undefined) {
        setValue(getBrandLogoUrl(slug));
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("solvait:branding-changed", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("solvait:branding-changed", onCustom);
    };
  }, [slug]);

  return value;
}
